import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  Plus,
  Search,
  Star,
  StarOff,
  Pencil,
  Trash2,
  Mail,
  Building2,
  Phone,
  MoreHorizontal,
  Upload,
  Download,
  History,
  Filter,
  X,
  Crown,
  Loader2,
  UserPlus,
  FolderOpen,
  LayoutGrid,
  List,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  ActionSheet,
  ActionSheetContent,
  ActionSheetItem,
  ActionSheetSeparator,
} from "@/components/ui/bottom-sheet";
import { Button, FloatingActionButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { contactsApi } from "@/services/api";
import { generateUUID } from "@/utils/transform";

export const ContactsPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [filterGroupId, setFilterGroupId] = useState("all");
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [importData, setImportData] = useState("");
  const [actionSheetContact, setActionSheetContact] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    company: "",
    title: "",
    phone: "",
    notes: "",
    groupIds: [],
  });

  // Queries
  const { data: contactsData, isLoading: isLoadingContacts } = useQuery({
    queryKey: ["contacts", searchQuery, filterGroupId, filterFavorites],
    queryFn: () =>
      contactsApi.list({
        search: searchQuery || undefined,
        groupId: filterGroupId !== "all" ? filterGroupId : undefined,
        favorites: filterFavorites ? true : undefined,
      }),
  });

  const { data: groupsData } = useQuery({
    queryKey: ["contact-groups"],
    queryFn: () => contactsApi.listGroups(),
  });

  const { data: limitsData } = useQuery({
    queryKey: ["contact-limits"],
    queryFn: () => contactsApi.getLimits(),
  });

  const { data: historyData, isLoading: isLoadingHistory } = useQuery({
    queryKey: ["contact-history", selectedContact?.id],
    queryFn: () => contactsApi.getHistory(selectedContact.id),
    enabled: !!selectedContact && isHistoryDialogOpen,
  });

  const contacts = contactsData?.data?.data || [];
  const groups = groupsData?.data?.data || [];
  const limits = limitsData?.data?.data || null;
  const history = historyData?.data?.data || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => contactsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contact-limits"] });
      toast.success(
        t("contacts.createSuccess", "Contact created successfully"),
      );
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
          t("contacts.createError", "Failed to create contact"),
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => contactsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast.success(
        t("contacts.updateSuccess", "Contact updated successfully"),
      );
      setIsEditDialogOpen(false);
      setSelectedContact(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
          t("contacts.updateError", "Failed to update contact"),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => contactsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contact-limits"] });
      toast.success(
        t("contacts.deleteSuccess", "Contact deleted successfully"),
      );
      setIsDeleteDialogOpen(false);
      setSelectedContact(null);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
          t("contacts.deleteError", "Failed to delete contact"),
      );
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: (id) => contactsApi.toggleFavorite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });

  const importMutation = useMutation({
    mutationFn: (contacts) => contactsApi.import(contacts),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contact-limits"] });
      const result = response.data;
      toast.success(
        t(
          "contacts.importSuccess",
          "Imported {{imported}} contacts ({{skipped}} skipped)",
          {
            imported: result.imported,
            skipped: result.skipped,
          },
        ),
      );
      setIsImportDialogOpen(false);
      setImportData("");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
          t("contacts.importError", "Failed to import contacts"),
      );
    },
  });

  // Helpers
  const resetForm = () => {
    setFormData({
      email: "",
      name: "",
      company: "",
      title: "",
      phone: "",
      notes: "",
      groupIds: [],
    });
  };

  const openEditDialog = (contact) => {
    setSelectedContact(contact);
    setFormData({
      email: contact.email,
      name: contact.name || "",
      company: contact.company || "",
      title: contact.title || "",
      phone: contact.phone || "",
      notes: contact.notes || "",
      groupIds: contact.groups?.map((g) => g.id) || [],
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (contact) => {
    setSelectedContact(contact);
    setIsDeleteDialogOpen(true);
  };

  const openHistoryDialog = (contact) => {
    setSelectedContact(contact);
    setIsHistoryDialogOpen(true);
  };

  const handleCreate = () => {
    if (!formData.email) {
      toast.error(t("contacts.emailRequired", "Email is required"));
      return;
    }
    createMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (!formData.email) {
      toast.error(t("contacts.emailRequired", "Email is required"));
      return;
    }
    updateMutation.mutate({ id: selectedContact.id, data: formData });
  };

  const handleDelete = () => {
    deleteMutation.mutate(selectedContact.id);
  };

  const handleImport = () => {
    try {
      // Parse CSV data
      const lines = importData.trim().split("\n");
      if (lines.length < 2) {
        toast.error(t("contacts.importInvalidFormat", "Invalid CSV format"));
        return;
      }

      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const emailIndex = headers.indexOf("email");
      if (emailIndex === -1) {
        toast.error(
          t("contacts.importNoEmail", "CSV must have an 'email' column"),
        );
        return;
      }

      const contacts = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map((v) => v.trim());
        if (values[emailIndex]) {
          contacts.push({
            email: values[emailIndex],
            name: values[headers.indexOf("name")] || "",
            company: values[headers.indexOf("company")] || "",
            title: values[headers.indexOf("title")] || "",
            phone: values[headers.indexOf("phone")] || "",
          });
        }
      }

      if (contacts.length === 0) {
        toast.error(t("contacts.importNoContacts", "No valid contacts found"));
        return;
      }

      importMutation.mutate(contacts);
    } catch {
      toast.error(t("contacts.importParseError", "Failed to parse CSV data"));
    }
  };

  const handleExport = async () => {
    try {
      const response = await contactsApi.export();
      const contacts = response.data?.data || [];

      // Convert to CSV
      const headers = [
        "email",
        "name",
        "company",
        "title",
        "phone",
        "notes",
        "groups",
      ];
      const rows = contacts.map((c) =>
        [c.email, c.name, c.company, c.title, c.phone, c.notes, c.groups].map(
          (v) => (v ? `"${v.replace(/"/g, '""')}"` : ""),
        ),
      );

      const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join(
        "\n",
      );
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contacts-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(
        t("contacts.exportSuccess", "Contacts exported successfully"),
      );
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          t("contacts.exportError", "Failed to export contacts"),
      );
    }
  };

  // Check if at limit
  const isAtContactLimit = Boolean(
    limits &&
    limits.contacts &&
    limits.contacts.limit !== -1 &&
    limits.contacts.remaining === 0,
  );

  const renderGridOrListOfContacts = () => {
    if (isLoadingContacts) {
      return viewMode === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map(() => (
            <Card key={generateUUID()}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="p-6 space-y-4">
              {[...Array(5)].map(() => (
                <div key={generateUUID()} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/4" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (contacts.length === 0) {
      return (
        <Card>
          <CardContent className="p-0">
            <div className="p-12 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mx-auto mb-4">
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">
                {searchQuery
                  ? t("contacts.noSearchResults", "No contacts found")
                  : t("contacts.noContacts", "No contacts yet")}
              </h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
                {searchQuery
                  ? t(
                      "contacts.tryDifferentSearch",
                      "Try a different search term",
                    )
                  : t(
                      "contacts.addFirstContact",
                      "Add your first contact to get started",
                    )}
              </p>
              {!searchQuery && (
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t("contacts.addContact", "Add Contact")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (viewMode === "grid") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {contacts.map((contact) => (
            <Card
              key={contact.id}
              className="group overflow-hidden card-pressable"
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <span className="text-lg font-semibold text-primary">
                        {(contact.name || contact.email)?.[0]?.toUpperCase() ||
                          "?"}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium truncate">
                        {contact.name || contact.email}
                      </h3>
                      {contact.title && (
                        <p className="text-sm text-muted-foreground truncate">
                          {contact.title}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => favoriteMutation.mutate(contact.id)}
                      className={`p-1.5 hover:bg-muted rounded transition-opacity touch-target ${isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                    >
                      {contact.isFavorite ? (
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ) : (
                        <StarOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                    {isMobile ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 touch-target"
                        onClick={() => setActionSheetContact(contact)}
                      >
                        <MoreHorizontal className="h-5 w-5" />
                      </Button>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => openEditDialog(contact)}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            {t("common.edit", "Edit")}
                          </DropdownMenuItem>
                          {limits?.features?.canViewHistory && (
                            <DropdownMenuItem
                              onClick={() => openHistoryDialog(contact)}
                            >
                              <History className="h-4 w-4 mr-2" />
                              {t("contacts.viewHistory", "View History")}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => openDeleteDialog(contact)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t("common.delete", "Delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" />
                    <span className="truncate">{contact.email}</span>
                  </div>
                  {contact.company && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Building2 className="h-3.5 w-3.5" />
                      <span className="truncate">{contact.company}</span>
                    </div>
                  )}
                  {contact.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      <span className="truncate">{contact.phone}</span>
                    </div>
                  )}
                </div>

                {contact.groups?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t">
                    {contact.groups.slice(0, 3).map((group) => (
                      <Badge
                        key={group.id}
                        variant="outline"
                        className="text-xs"
                        style={{
                          borderColor: group.color,
                          color: group.color,
                        }}
                      >
                        {group.name}
                      </Badge>
                    ))}
                    {contact.groups.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{contact.groups.length - 3}
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    return (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12" />
              <TableHead>{t("contacts.name", "Name")}</TableHead>
              <TableHead>{t("contacts.email", "Email")}</TableHead>
              <TableHead>{t("contacts.company", "Company")}</TableHead>
              <TableHead>{t("contacts.groups", "Groups")}</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell>
                  <button
                    onClick={() => favoriteMutation.mutate(contact.id)}
                    className="p-1 hover:bg-muted rounded"
                  >
                    {contact.isFavorite ? (
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ) : (
                      <StarOff className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <span className="text-sm font-medium text-primary">
                        {(contact.name || contact.email)?.[0]?.toUpperCase() ||
                          "?"}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium">{contact.name || "-"}</div>
                      {contact.title && (
                        <div className="text-sm text-muted-foreground">
                          {contact.title}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    {contact.email}
                  </div>
                </TableCell>
                <TableCell>
                  {contact.company ? (
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {contact.company}
                    </div>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {contact.groups?.map((group) => (
                      <Badge
                        key={group.id}
                        variant="outline"
                        style={{
                          borderColor: group.color,
                          color: group.color,
                        }}
                      >
                        {group.name}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(contact)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        {t("common.edit", "Edit")}
                      </DropdownMenuItem>
                      {limits?.features?.canViewHistory && (
                        <DropdownMenuItem
                          onClick={() => openHistoryDialog(contact)}
                        >
                          <History className="h-4 w-4 mr-2" />
                          {t("contacts.viewHistory", "View History")}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => openDeleteDialog(contact)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t("common.delete", "Delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  const renderVariant = (item) => {
    if (item?.status === "completed") {
      return "default";
    }
    return item?.status === "pending" ? "secondary" : "outline";
  };

  const renderHistoryLog = () => {
    if (isLoadingHistory) {
      return (
        <div className="space-y-4">
          {[...Array(3)].map(() => (
            <Skeleton key={generateUUID()} className="h-16 w-full" />
          ))}
        </div>
      );
    }
    return history.length === 0 ? (
      <div className="py-8 text-center text-muted-foreground">
        {t("contacts.noHistory", "No signing history found for this contact")}
      </div>
    ) : (
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {history.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between p-3 border rounded-lg"
          >
            <div>
              <Link
                to={`/envelopes/${item.id}`}
                className="font-medium hover:underline"
              >
                {item.title}
              </Link>
              <p className="text-sm text-muted-foreground">
                {new Date(item.createdAt).toLocaleDateString()}
              </p>
            </div>
            <Badge variant={renderVariant(item)}>{item.status}</Badge>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("contacts.title", "Contacts")}
          </h1>
          <p className="text-muted-foreground">
            {t(
              "contacts.description",
              "Manage your contacts for quick recipient selection",
            )}
          </p>
        </div>
        {!isMobile && (
          <div className="flex items-center gap-2">
            {limits?.features?.canImportExport && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsImportDialogOpen(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {t("contacts.import", "Import")}
                </Button>
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  {t("contacts.export", "Export")}
                </Button>
              </>
            )}
            <Link to="/contacts/groups">
              <Button variant="outline" size="sm">
                <FolderOpen className="h-4 w-4 mr-2" />
                {t("contacts.manageGroups", "Groups")}
              </Button>
            </Link>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      onClick={() => setIsCreateDialogOpen(true)}
                      disabled={isAtContactLimit}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t("contacts.addContact", "Add Contact")}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isAtContactLimit && (
                  <TooltipContent>
                    <p>
                      {t(
                        "contacts.limitReached",
                        "Contact limit reached. Upgrade your plan.",
                      )}
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>

      {/* Limits Card */}
      {limits && limits.contacts && limits.contacts.limit !== -1 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("contacts.usage", "Contact Usage")}
                  </p>
                  <p className="text-lg font-semibold">
                    {limits.contacts.current} / {limits.contacts.limit}
                  </p>
                </div>
                {limits.groups?.limit > 0 && (
                  <div className="border-l pl-4">
                    <p className="text-sm text-muted-foreground">
                      {t("contacts.groupUsage", "Group Usage")}
                    </p>
                    <p className="text-lg font-semibold">
                      {limits.groups.current} / {limits.groups.limit}
                    </p>
                  </div>
                )}
              </div>
              {isAtContactLimit && (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/settings/billing/plans">
                    <Crown className="h-4 w-4 mr-2" />
                    {t("common.upgrade", "Upgrade")}
                  </Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center flex-1">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t(
                "contacts.searchPlaceholder",
                "Search by name, email, or company...",
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={isMobile ? "pl-10 h-11" : "pl-10"}
            />
            {searchQuery && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 touch-target"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterGroupId} onValueChange={setFilterGroupId}>
              <SelectTrigger className={isMobile ? "flex-1 h-11" : "w-45"}>
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue
                  placeholder={t("contacts.filterByGroup", "Filter by group")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("contacts.allGroups", "All Groups")}
                </SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: group.color }}
                      />
                      {group.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={filterFavorites ? "secondary" : "outline"}
              size={isMobile ? "mobile-icon" : "icon"}
              onClick={() => setFilterFavorites(!filterFavorites)}
            >
              <Star
                className={`h-4 w-4 ${filterFavorites ? "fill-yellow-400 text-yellow-400" : ""}`}
              />
            </Button>
          </div>
        </div>

        {/* View toggle - hide on mobile */}
        {!isMobile && (
          <div className="flex items-center border rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Contacts Grid/List */}
      {renderGridOrListOfContacts()}

      {/* Create/Edit Dialog */}
      <Dialog
        open={isCreateDialogOpen || isEditDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
            setSelectedContact(null);
            resetForm();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditDialogOpen
                ? t("contacts.editContact", "Edit Contact")
                : t("contacts.addContact", "Add Contact")}
            </DialogTitle>
            <DialogDescription>
              {isEditDialogOpen
                ? t("contacts.editDescription", "Update the contact details")
                : t(
                    "contacts.createDescription",
                    "Add a new contact to your address book",
                  )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("contacts.email", "Email")} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="john@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t("contacts.name", "Name")}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="John Doe"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company">
                  {t("contacts.company", "Company")}
                </Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) =>
                    setFormData({ ...formData, company: e.target.value })
                  }
                  placeholder="Acme Inc"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">
                  {t("contacts.jobTitle", "Job Title")}
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="CEO"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t("contacts.phone", "Phone")}</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="+1 234 567 8900"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">{t("contacts.notes", "Notes")}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder={t(
                  "contacts.notesPlaceholder",
                  "Add any notes about this contact...",
                )}
                rows={3}
              />
            </div>
            {groups.length > 0 && (
              <div className="space-y-2">
                <Label>{t("contacts.groups", "Groups")}</Label>
                <div className="flex flex-wrap gap-2">
                  {groups.map((group) => (
                    <Badge
                      key={group.id}
                      variant={
                        formData.groupIds.includes(group.id)
                          ? "default"
                          : "outline"
                      }
                      className="cursor-pointer"
                      style={
                        formData.groupIds.includes(group.id)
                          ? { backgroundColor: group.color }
                          : { borderColor: group.color, color: group.color }
                      }
                      onClick={() => {
                        const newGroupIds = formData.groupIds.includes(group.id)
                          ? formData.groupIds.filter((id) => id !== group.id)
                          : [...formData.groupIds, group.id];
                        setFormData({ ...formData, groupIds: newGroupIds });
                      }}
                    >
                      {group.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                setIsEditDialogOpen(false);
                resetForm();
              }}
              size={isMobile ? "mobile" : "default"}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={isEditDialogOpen ? handleUpdate : handleCreate}
              disabled={createMutation.isPending || updateMutation.isPending}
              size={isMobile ? "mobile" : "default"}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {isEditDialogOpen
                ? t("common.save", "Save")
                : t("common.create", "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("contacts.deleteContact", "Delete Contact")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "contacts.deleteConfirm",
                "Are you sure you want to delete this contact? This action cannot be undone.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              size={isMobile ? "mobile" : "default"}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              size={isMobile ? "mobile" : "default"}
            >
              {deleteMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t("common.delete", "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t("contacts.signingHistory", "Signing History")}
            </DialogTitle>
            <DialogDescription>
              {selectedContact && (
                <>
                  {t("contacts.historyFor", "Documents sent to")}{" "}
                  {selectedContact.email}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {renderHistoryLog()}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("contacts.importContacts", "Import Contacts")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "contacts.importDescription",
                "Paste CSV data with columns: email, name, company, title, phone",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder="email,name,company,title,phone&#10;john@example.com,John Doe,Acme Inc,CEO,+1234567890"
              rows={10}
              className="font-mono text-sm"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsImportDialogOpen(false)}
              size={isMobile ? "mobile" : "default"}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleImport}
              disabled={importMutation.isPending || !importData.trim()}
              size={isMobile ? "mobile" : "default"}
            >
              {importMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t("contacts.import", "Import")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile FAB */}
      {isMobile && (
        <FloatingActionButton
          onClick={() => !isAtContactLimit && setIsCreateDialogOpen(true)}
          disabled={isAtContactLimit}
        >
          <Plus className="h-6 w-6" />
        </FloatingActionButton>
      )}

      {/* Mobile ActionSheet for contact actions */}
      {isMobile && (
        <ActionSheet
          open={!!actionSheetContact}
          onOpenChange={(open) => !open && setActionSheetContact(null)}
        >
          <ActionSheetContent
            title={actionSheetContact?.name || actionSheetContact?.email}
          >
            <ActionSheetItem
              onClick={() => {
                openEditDialog(actionSheetContact);
                setActionSheetContact(null);
              }}
            >
              <Pencil className="h-5 w-5" />
              {t("common.edit", "Edit")}
            </ActionSheetItem>
            <ActionSheetItem
              onClick={() => {
                favoriteMutation.mutate(actionSheetContact.id);
                setActionSheetContact(null);
              }}
            >
              {actionSheetContact?.isFavorite ? (
                <StarOff className="h-5 w-5" />
              ) : (
                <Star className="h-5 w-5" />
              )}
              {actionSheetContact?.isFavorite
                ? t("contacts.unfavorite", "Remove from favorites")
                : t("contacts.favorite", "Add to favorites")}
            </ActionSheetItem>
            {limits?.features?.canViewHistory && (
              <ActionSheetItem
                onClick={() => {
                  openHistoryDialog(actionSheetContact);
                  setActionSheetContact(null);
                }}
              >
                <History className="h-5 w-5" />
                {t("contacts.viewHistory", "View History")}
              </ActionSheetItem>
            )}
            <ActionSheetSeparator />
            <ActionSheetItem
              destructive
              onClick={() => {
                openDeleteDialog(actionSheetContact);
                setActionSheetContact(null);
              }}
            >
              <Trash2 className="h-5 w-5" />
              {t("common.delete", "Delete")}
            </ActionSheetItem>
          </ActionSheetContent>
        </ActionSheet>
      )}
    </div>
  );
};

export default ContactsPage;
